import React, { useEffect, useState, useCallback } from "react";
const API = process.env.REACT_APP_API_BASE_URL || "https://zoompay.co.ke";

const KYC_STATUS_BADGE = {
  verified:       'bg-green-100 text-green-800',
  pending:        'bg-yellow-100 text-yellow-800',
  requires_input: 'bg-orange-100 text-orange-800',
  failed:         'bg-red-100 text-red-800',
  not_started:    'bg-gray-100 text-gray-600',
};
const getToken = () => localStorage.getItem("adminToken");

const customerNumber = (u) => {
  const country = (u?.country || "CA").toUpperCase();
  const prefix = country === "US" ? "ZP-US" : "ZP-CA";
  const compact = (u?.id || "").replace(/-/g, "").toUpperCase();
  return compact ? `${prefix}-${compact.slice(0, 8)}` : `${prefix}-UNKNOWN`;
};

export default function CockpitKYC() {
  const [kyc, setKyc] = useState([]);
  const [kycLoading, setKycLoading] = useState(true);
  const [kycTotal, setKycTotal] = useState(0);
  const [kycOffset, setKycOffset] = useState(0);
  const [kycSearchInput, setKycSearchInput] = useState("");
  const [kycSearch, setKycSearch] = useState("");
  const [kycStatusFilter, setKycStatusFilter] = useState("");
  const [kycProviderFilter, setKycProviderFilter] = useState("");
  const [selectedKyc, setSelectedKyc] = useState(null);
  const kycLimit = 25;

  const loadKyc = useCallback(() => {
    setKycLoading(true);
    const params = new URLSearchParams({
      limit: kycLimit,
      offset: kycOffset,
      ...(kycSearch ? { search: kycSearch } : {}),
      ...(kycStatusFilter ? { status: kycStatusFilter } : {}),
      ...(kycProviderFilter ? { provider: kycProviderFilter } : {}),
    });
    fetch(`${API}/api/v2/cockpit/kyc?${params}`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then(r => r.json())
      .then(d => {
        if (d.ok) {
          setKyc(d.kyc || []);
          setKycTotal(d.total || 0);
        }
      })
      .finally(() => setKycLoading(false));
  }, [kycOffset, kycSearch, kycStatusFilter, kycProviderFilter]);

  useEffect(() => { loadKyc(); }, [loadKyc]);

  const handleKycSearchSubmit = (e) => {
    e.preventDefault();
    setKycOffset(0);
    setKycSearch(kycSearchInput.trim());
  };

  return (
    <div className="p-6 space-y-6">

      {selectedKyc && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setSelectedKyc(null)}
        >
          <div
            className="bg-white rounded-lg shadow-xl p-6 max-w-lg w-full mx-4 max-h-screen overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">KYC Details</h2>
              <button onClick={() => setSelectedKyc(null)} className="text-gray-400 hover:text-gray-600 text-2xl">×</button>
            </div>

            <div className="space-y-2 text-sm">
              {[
                ["Customer #", customerNumber(selectedKyc)],
                ["User", `${selectedKyc.first_name || ""} ${selectedKyc.last_name || ""}`.trim() || "—"],
                ["Email", selectedKyc.email || "—"],
                ["Phone", selectedKyc.phone || "—"],
                
                ["Account Status", selectedKyc.account_status ? selectedKyc.account_status.toUpperCase() : "—"],
                ["Customer Since", selectedKyc.customer_created_at ? new Date(selectedKyc.customer_created_at).toLocaleDateString("en-CA") : "—"],
                ["Country", selectedKyc.country || "—"],
                ["Email Verified", selectedKyc.email_verified ? "YES" : "NO"],
                ["Phone Verified", selectedKyc.phone_verified ? "YES" : "NO"],
                ["Identity Verified", selectedKyc.id_verified ? "YES" : "NO"],
                ["KYC Status", selectedKyc.kyc_status ? selectedKyc.kyc_status.replace(/_/g, " ").toUpperCase() : "NOT STARTED"],
                ["Provider", selectedKyc.kyc_provider || "—"],
                ["Document Type", selectedKyc.doc_type || "—"],
                ["Jurisdiction", selectedKyc.doc_jurisdiction || "—"],
                ["Street Address", selectedKyc.street_address || "—"],
                ["City", selectedKyc.city || "—"],
                ["Province/State", selectedKyc.state || "—"],
                ["Postal/Zip Code", selectedKyc.zip_code || "—"],
                ["Verified On", selectedKyc.verification_date ? new Date(selectedKyc.verification_date).toLocaleString("en-CA") : "—"],
                ["Provider Session ID", selectedKyc.provider_session_id || selectedKyc.stripe_session_id || "—"],
                ["Developer ID", selectedKyc.id ? selectedKyc.id.slice(0,8) + "..." : "—"],
              ].map(([label, value]) => (
                <div key={label} className="flex justify-between border-b pb-1 gap-4">
                  <span className="text-gray-500 w-1/3">{label}</span>
                  <span className="text-right w-2/3 break-all">{value}</span>
                </div>
              ))}
            </div>

            <div className="border-t pt-4 mt-4 space-y-2">
              <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide">Support Actions</p>
              <button
                disabled
                className="w-full bg-gray-100 text-gray-400 py-2 px-4 rounded text-sm font-semibold cursor-not-allowed"
              >
                Send Secure Update Link — Coming Soon
              </button>
              <p className="text-xs text-gray-400">
                CS should send secure customer update links for address, expired ID, or phone changes. Direct edits should require Compliance/Admin approval and audit logging.
              </p>
            </div>
          </div>
        </div>
      )}

      <div>
        <h1 className="text-2xl font-bold">KYC Verification</h1>
        <p className="text-gray-500 text-sm">FINTRAC document verification records</p>
      </div>

      <div className="bg-white border rounded-lg shadow-sm overflow-x-auto">
        <div className="px-4 py-3 border-b bg-gray-50 flex flex-wrap items-center gap-2">
          <form onSubmit={handleKycSearchSubmit} className="flex items-center gap-2">
            <input
              type="text"
              value={kycSearchInput}
              onChange={e => setKycSearchInput(e.target.value)}
              placeholder="Search name or email..."
              className="border border-gray-300 rounded px-3 py-1.5 text-sm w-56"
            />
            <button type="submit" className="px-3 py-1.5 text-sm rounded border border-gray-300 bg-white hover:bg-gray-50">
              Search
            </button>
          </form>
          <select
            value={kycStatusFilter}
            onChange={e => { setKycOffset(0); setKycStatusFilter(e.target.value); }}
            className="border border-gray-300 rounded px-2 py-1.5 text-sm"
          >
            <option value="">All Statuses</option>
            <option value="verified">Verified</option>
            <option value="failed">Failed</option>
            <option value="pending">Pending</option>
            <option value="manual_review">Manual Review</option>
          </select>
          <select
            value={kycProviderFilter}
            onChange={e => { setKycOffset(0); setKycProviderFilter(e.target.value); }}
            className="border border-gray-300 rounded px-2 py-1.5 text-sm"
          >
            <option value="">All Providers</option>
            <option value="veriff">Veriff</option>
            <option value="stripe">Stripe</option>
          </select>
          {(kycSearch || kycStatusFilter || kycProviderFilter) && (
            <button
              onClick={() => { setKycSearchInput(""); setKycSearch(""); setKycStatusFilter(""); setKycProviderFilter(""); setKycOffset(0); }}
              className="px-3 py-1.5 text-sm text-blue-600 hover:underline"
            >
              Clear filters
            </button>
          )}
        </div>
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              {["User","Country","KYC Status","Provider","Doc Type","Jurisdiction","Verified On","Session ID","Actions"].map(h => (
                <th key={h} className="px-3 py-3 text-left text-gray-600 font-medium">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y">
            {kycLoading && <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-400">Loading KYC records...</td></tr>}
            {kyc.map(u => (
              <tr key={u.id} className="hover:bg-gray-50">
                <td className="px-3 py-3">
                  <div className="font-medium">{u.first_name} {u.last_name}</div>
                  <div className="text-xs text-gray-400">{u.email}</div>
                </td>
                <td className="px-3 py-3">{u.country}</td>
                <td className="px-3 py-3">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${KYC_STATUS_BADGE[u.kyc_status] || KYC_STATUS_BADGE.not_started}`}>
                    {u.kyc_status ? u.kyc_status.replace(/_/g, ' ').toUpperCase() : 'NOT STARTED'}
                  </span>
                </td>
                <td className="px-3 py-3">
                  {u.kyc_provider ? (
                    <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 capitalize">{u.kyc_provider}</span>
                  ) : '—'}
                </td>
                <td className="px-3 py-3 text-gray-600">{u.doc_type || '—'}</td>
                <td className="px-3 py-3 text-gray-600">{u.doc_jurisdiction || '—'}</td>
                <td className="px-3 py-3 text-gray-500">{u.verification_date ? new Date(u.verification_date).toLocaleDateString('en-CA') : '—'}</td>
                <td className="px-3 py-3 text-xs text-gray-400 font-mono">{(u.provider_session_id || u.stripe_session_id) ? (u.provider_session_id || u.stripe_session_id).slice(0,20) + '...' : '—'}</td>
                <td className="px-3 py-3">
                  <button
                    onClick={() => setSelectedKyc(u)}
                    className="text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
                  >
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="px-4 py-3 border-t flex items-center justify-between text-sm">
          <button
            disabled={kycOffset === 0}
            onClick={() => setKycOffset(Math.max(0, kycOffset - kycLimit))}
            className={`px-3 py-1.5 rounded border ${kycOffset === 0 ? "text-gray-300 border-gray-200" : "text-gray-700 border-gray-300 hover:bg-gray-50"}`}
          >
            Previous
          </button>
          <span className="text-gray-500">
            {kycTotal === 0 ? "0" : kycOffset + 1}-{Math.min(kycOffset + kycLimit, kycTotal)} of {kycTotal}
          </span>
          <button
            disabled={kycOffset + kycLimit >= kycTotal}
            onClick={() => setKycOffset(kycOffset + kycLimit)}
            className={`px-3 py-1.5 rounded border ${kycOffset + kycLimit >= kycTotal ? "text-gray-300 border-gray-200" : "text-gray-700 border-gray-300 hover:bg-gray-50"}`}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
